import { useEffect, useMemo, useState } from 'react';
import type { EmojiSection } from '../data/emoji.js';
import { getRecentEmoji, rememberRecentEmoji } from '../lib/storage.js';

interface EmojiPickerProps {
  onPick: (emoji: string) => void;
}

/**
 * Панель значков.
 *
 * Набор полный — весь Unicode, разложенный по тем же разделам и в том же
 * порядке, что в телеграме. Раньше здесь лежал список, написанный руками:
 * десятков шесть штук по выдуманным разделам.
 *
 * Данные весят сотню килобайт, поэтому грузятся отдельным куском и только
 * когда панель открыли: класть их в основную сборку значит замедлить
 * загрузку сайта ради того, что открывают не все.
 */
export function EmojiPicker({ onPick }: EmojiPickerProps): JSX.Element {
  const [sections, setSections] = useState<EmojiSection[] | null>(null);
  const [active, setActive] = useState(0);
  const [recent, setRecent] = useState<string[]>(getRecentEmoji);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    void import('../data/emoji.js').then((module) => {
      if (!cancelled) setSections(module.EMOJI_SECTIONS);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const found = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle || !sections) return null;
    const result: string[] = [];
    for (const section of sections) {
      for (const [emoji, name] of section.items) {
        if (name.includes(needle)) result.push(emoji);
        if (result.length >= 120) return result;
      }
    }
    return result;
  }, [query, sections]);

  const pick = (emoji: string): void => {
    setRecent(rememberRecentEmoji(emoji));
    onPick(emoji);
  };

  if (!sections) return <p className="stickers__empty">Загружаем…</p>;

  const shown =
    found ?? (active === -1 ? recent : (sections[active]?.items.map(([emoji]) => emoji) ?? []));

  return (
    <div className="emoji-panel">
      <input
        className="emoji-panel__search"
        type="search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Поиск: fire, heart, cat…"
        aria-label="Поиск значка по английскому названию"
      />

      {!found && (
        <div className="emoji-picker__tabs" role="tablist" aria-label="Разделы значков">
          {recent.length > 0 && (
            <button
              type="button"
              role="tab"
              aria-selected={active === -1}
              aria-label="Недавние"
              title="Недавние"
              className="pill"
              data-on={active === -1 ? '' : undefined}
              onClick={() => setActive(-1)}
            >
              🕘
            </button>
          )}
          {sections.map((section, index) => (
            <button
              key={section.title}
              type="button"
              role="tab"
              aria-selected={active === index}
              aria-label={section.title}
              title={section.title}
              className="pill"
              data-on={active === index ? '' : undefined}
              onClick={() => setActive(index)}
            >
              {section.icon}
            </button>
          ))}
        </div>
      )}

      <div className="emoji-picker__grid">
        {shown.map((emoji) => (
          <button
            key={emoji}
            type="button"
            className="emoji-picker__item"
            aria-label={`Вставить ${emoji}`}
            onClick={() => pick(emoji)}
          >
            {emoji}
          </button>
        ))}
      </div>

      {shown.length === 0 && (
        <p className="stickers__empty">
          {found ? 'Ничего не нашлось. Ищите по-английски: fire, heart, cat.' : 'Пока пусто.'}
        </p>
      )}
    </div>
  );
}
